import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  LinearProgress,
  Link,
  Snackbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import StarsOutlinedIcon from "@mui/icons-material/StarsOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import EventRepeatOutlinedIcon from "@mui/icons-material/EventRepeatOutlined";
import FeedbackOutlinedIcon from "@mui/icons-material/FeedbackOutlined";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

import {
  fetchCurrentUserProfile,
  saveJiraTask,
  getAllTasksForUser,
  completeJiraTask,
  sendMetricsReports,
  updateJiraMetricsTask,
} from "../api/services";
import ManagerFeedbackModal from "../components/ManagerFeedbackModal";

import TaskFormDialog from "../components/TaskFormDialog";
import CompleteTaskDialog from "../components/CompleteTaskDialog";
import TaskList from "../components/TaskList";

// ---- Tokens used by the main file only ----
const ui = {
  primary: "#009db9",
  primaryDark: "#007a8a",
  primarySoft: "#e6f6f9",
  text: "#1f2933",
  muted: "#6b7280",
  border: "#e5e7eb",
  surface: "#f7f9fb",
  success: "#2e7d32",
  successSoft: "#e8f5e9",
  danger: "#d32f2f",
  dangerSoft: "#ffebee",
  warning: "#b26a00",
  warningSoft: "#fff4e5",
  radius: "16px",
  shadow: "0 4px 20px rgba(16, 24, 40, 0.06)",
};

const primaryButtonSx = {
  backgroundColor: ui.primary,
  color: "#fff",
  borderRadius: "10px",
  textTransform: "none",
  fontWeight: 700,
  px: 3,
  py: 1.25,
  boxShadow: "none",
  "&:hover": { backgroundColor: ui.primaryDark, boxShadow: "none" },
  "&.Mui-disabled": { backgroundColor: "#d5dbe0", color: "#8a949e" },
};

const secondaryButtonSx = {
  textTransform: "none",
  fontWeight: 600,
  borderRadius: "10px",
  color: ui.primary,
  borderColor: ui.primary,
  px: 2.5,
  "&:hover": { borderColor: ui.primaryDark, backgroundColor: ui.primarySoft },
  "&.Mui-disabled": { color: "#b0b7be", borderColor: "#e0e4e8" },
};

const PageHeader = ({ title, subtitle }) => (
  <Box sx={{ mb: { xs: 3, md: 4 } }}>
    <Typography
      variant="h4"
      sx={{
        fontWeight: "bold",
        color: ui.text,
        letterSpacing: 0.5,
        fontSize: { xs: "1.5rem", sm: "1.85rem", md: "2.125rem" },
        position: "relative",
        wordWrap: "break-word",
        "&::after": {
          content: '""',
          display: "block",
          backgroundColor: ui.primary,
          height: "4px",
          width: { xs: "80px", sm: "100px" },
          position: "absolute",
          left: 0,
          bottom: "-8px",
        },
      }}
    >
      {title}
    </Typography>
    {subtitle && (
      <Typography
        sx={{ color: ui.muted, mt: 2.5, fontSize: { xs: "0.9rem", sm: "1rem" } }}
      >
        {subtitle}
      </Typography>
    )}
  </Box>
);

const SectionCard = ({ title, action, children, sx }) => (
  <Box
    sx={{
      borderRadius: ui.radius,
      border: `1px solid ${ui.border}`,
      backgroundColor: "#fff",
      boxShadow: ui.shadow,
      p: { xs: 2, sm: 3 },
      ...sx,
    }}
  >
    {title && (
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          gap: 2,
          mb: 2.5,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: ui.text,
            pl: 1.5,
            borderLeft: `4px solid ${ui.primary}`,
            lineHeight: 1.3,
          }}
        >
          {title}
        </Typography>
        {action}
      </Box>
    )}
    {children}
  </Box>
);

const StatGrid = ({ children, ...rest }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: {
        xs: "minmax(0, 1fr)",
        sm: "repeat(3, minmax(0, 1fr))",
      },
      gap: { xs: 1.5, md: 2 },
      mb: { xs: 2.5, md: 4 },
    }}
    {...rest}
  >
    {children}
  </Box>
);

const statTones = {
  primary: { bg: ui.primarySoft, color: ui.primary },
  success: { bg: ui.successSoft, color: ui.success },
  warning: { bg: ui.warningSoft, color: ui.warning },
};

const StatCard = ({ label, value, icon: Icon, tone = "primary" }) => {
  const t = statTones[tone] || statTones.primary;
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        p: 2,
        minWidth: 0,
        borderRadius: "14px",
        border: `1px solid ${ui.border}`,
        backgroundColor: "#fff",
        boxShadow: ui.shadow,
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 8px 24px rgba(16, 24, 40, 0.1)",
        },
      }}
    >
      {Icon && (
        <Box
          sx={{
            flexShrink: 0,
            width: 44,
            height: 44,
            borderRadius: "12px",
            backgroundColor: t.bg,
            color: t.color,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon />
        </Box>
      )}
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ color: ui.muted }} noWrap>
          {label}
        </Typography>
        <Typography
          sx={{
            fontWeight: 700,
            color: ui.text,
            fontSize: { xs: "1.2rem", md: "1.35rem" },
            lineHeight: 1.3,
            wordBreak: "break-word",
          }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );
};

const EmployeeMetrics = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [formData, setFormData] = useState({
    taskId: "",
    title: "",
    link: "",
    storyPoints: "",
    dueDate: dayjs(),
  });
  const [openForm, setOpenForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({
    taskId: "",
    title: "",
    link: "",
    storyPoints: "",
    dueDate: "",
  });
  const [submitError, setSubmitError] = useState("");
  const [currentUser, setCurrentUser] = useState(null);
  const [fetchUserError, setFetchUserError] = useState("");

  const [tasks, setTasks] = useState([]);
  const [isTasksLoading, setIsTasksLoading] = useState(false);
  const [tasksError, setTasksError] = useState("");
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [, setTotalPages] = useState(1);

  const [isSendingReport, setIsSendingReport] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const [openCompleteDialog, setOpenCompleteDialog] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [completedDate, setCompletedDate] = useState(dayjs());

  const [isEditing, setIsEditing] = useState(false);
  const [editingTask, setEditingTask] = useState(null);

  const [openManagerFeedback, setOpenManagerFeedback] = useState(false);

  const observer = useRef();

  const lastTaskElementRef = useCallback(
    (node) => {
      if (isTasksLoading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prevPage) => prevPage + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [isTasksLoading, hasMore]
  );

  const totalStoryPoints = Array.isArray(tasks)
    ? tasks.reduce(
        (sum, task) =>
          task.completed && task.status === "Done"
            ? sum + (task.actualPoints || task.storyPoints || 0)
            : sum,
        0
      )
    : 0;

  const completedTasksCount = Array.isArray(tasks)
    ? tasks.filter((task) => task.completed && task.status === "Done").length
    : 0;

  const currentDate = dayjs().format("MMM D, YYYY");

  const getCurrentCycle = () => {
    const startDate = dayjs("2025-12-01");
    const today = dayjs();
    const daysDiff = today.diff(startDate, "day");
    const cycleNumber = Math.floor(daysDiff / 14) + 1;
    const cycleStart = startDate.add((cycleNumber - 1) * 14, "day");
    const cycleEnd = cycleStart.add(13, "day");

    return {
      number: cycleNumber,
      display: `Cycle ${cycleNumber}: ${cycleStart.format("MMM D")}–${cycleEnd.format("D, YYYY")}`,
      cycleStart,
      cycleEnd,
    };
  };

  const currentCycle = getCurrentCycle();

  const isSaturdayOfWeek2 = () => {
    const today = dayjs();
    const daysDiff = today.diff(currentCycle.cycleStart, "day");
    return daysDiff === 12 && today.day() === 6;
  };

  const TARGET_POINTS = 60;
  const currentPoints = totalStoryPoints;
  const pointsNeeded = Math.max(0, TARGET_POINTS - currentPoints);
  const progressPercentage = (currentPoints / TARGET_POINTS) * 100;

  const getEligibilityStatus = () => {
    if (progressPercentage >= 120)
      return { status: "Eligible +", color: "#2e7d32" };
    if (progressPercentage >= 91)
      return { status: "Eligible", color: "#2e7d32" };
    if (progressPercentage >= 80)
      return { status: "Potentially Eligible", color: "#ed6c02" };
    return { status: "Not Eligible", color: "#d32f2f" };
  };

  const eligibility = getEligibilityStatus();

  const fetchTasks = async (userId, pageNumber) => {
    setIsTasksLoading(true);
    setTasksError("");
    try {
      const userTasks = await getAllTasksForUser(
        userId,
        pageNumber,
        10,
        "createdDate,desc"
      );
      const tasksArray = Array.isArray(userTasks?.data?.content)
        ? userTasks.data.content
        : [];
      setTotalPages(userTasks?.data?.totalPages || 1);
      setHasMore(userTasks?.data?.last === false);
      setTasks((prevTasks) =>
        pageNumber === 0 ? tasksArray : [...prevTasks, ...tasksArray]
      );
    } catch (error) {
      setTasksError("Failed to fetch tasks. Please try again.");
      console.error("Error fetching tasks:", error);
      if (pageNumber === 0) setTasks([]);
    } finally {
      setIsTasksLoading(false);
    }
  };

  const handleCompleteTask = (taskId) => {
    setSelectedTaskId(taskId);
    setCompletedDate(dayjs());
    setOpenCompleteDialog(true);
  };

  const handleConfirmCompleteTask = async () => {
    try {
      const formattedDate = completedDate
        ? completedDate.format("YYYY-MM-DDTHH:mm:ss")
        : null;
      await completeJiraTask(selectedTaskId, formattedDate);
      setPage(0);
      setTasks([]);
      await fetchTasks(currentUser.id, 0);
      setSnackbarMessage("Task completed successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Failed to complete task:", error);
      setSnackbarMessage("Failed to complete task. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setOpenCompleteDialog(false);
      setSelectedTaskId(null);
    }
  };

  const handleSendReport = async () => {
    setIsSendingReport(true);
    try {
      await sendMetricsReports();
      setSnackbarMessage("Metrics report sent successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      setSnackbarMessage("Failed to send report. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      console.error("Error sending report:", error);
    } finally {
      setIsSendingReport(false);
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleOpenManagerFeedback = () => {
    setOpenManagerFeedback(true);
  };

  const handleCloseManagerFeedback = () => {
    setOpenManagerFeedback(false);
  };

  useEffect(() => {
    const initData = async () => {
      try {
        const userProfile = await fetchCurrentUserProfile();
        setCurrentUser(userProfile);
      } catch (error) {
        setFetchUserError(
          "Failed to load user profile. Cannot add new tasks or view existing ones."
        );
        console.error("Error fetching user profile:", error);
      }
    };
    initData();
  }, []);

  useEffect(() => {
    if (currentUser && currentUser.id) {
      fetchTasks(currentUser.id, page);
    }
  }, [currentUser, page]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
    setSubmitError("");
  };

  const handleDateChange = (date) => {
    setFormData((prevData) => ({
      ...prevData,
      dueDate: date,
    }));
    setErrors((prevErrors) => ({
      ...prevErrors,
      dueDate: "",
    }));
    setSubmitError("");
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;
    if (!formData.taskId.trim()) {
      newErrors.taskId = "Task ID is required";
      isValid = false;
    }
    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
      isValid = false;
    }
    if (!formData.link.trim()) {
      newErrors.link = "Link is required";
      isValid = false;
    } else if (
      !/^(https?:\/\/)?([\w-]+(\.[\w-]+)+)([\w.,@?^=%&:/~+#-]*[\w@?^=%&/~+#-])?$/.test(
        formData.link
      )
    ) {
      newErrors.link = "Please enter a valid URL";
      isValid = false;
    }
    if (
      formData.storyPoints === "" ||
      isNaN(formData.storyPoints) ||
      Number(formData.storyPoints) < 0
    ) {
      newErrors.storyPoints = "Story Points must be a non-negative number";
      isValid = false;
    }
    if (!formData.dueDate || !formData.dueDate.isValid()) {
      newErrors.dueDate = "Valid due date and time is required";
      isValid = false;
    }
    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!currentUser) {
      setSubmitError("User profile not loaded. Please try again.");
      return;
    }
    if (validateForm()) {
      setLoading(true);
      setSubmitError("");
      try {
        if (isEditing && editingTask) {
          const payload = {
            taskId: formData.taskId,
            title: formData.title,
            link: formData.link,
            storyPoints: Number(formData.storyPoints),
            dueDate: formData.dueDate
              ? formData.dueDate.format("YYYY-MM-DDTHH:mm:ss")
              : null,
          };

          await updateJiraMetricsTask(currentUser.id, editingTask.id, payload);
          setSnackbarMessage("Task updated successfully!");
        } else {
          const payload = {
            ...formData,
            sprint: "Current Sprint",
            dueDate: formData.dueDate
              ? formData.dueDate.format("YYYY-MM-DDTHH:mm:ss")
              : null,
            assignedUserId: currentUser.id,
            storyPoints: Number(formData.storyPoints),
          };
          await saveJiraTask(payload);
          setSnackbarMessage("Task added successfully!");
        }

        setSnackbarSeverity("success");
        setOpenForm(false);
        setFormData({
          taskId: "",
          title: "",
          link: "",
          storyPoints: "",
          dueDate: dayjs(),
        });
        setErrors({});
        setSubmitError("");
        setIsEditing(false);
        setEditingTask(null);
        setPage(0);
        setTasks([]);
        await fetchTasks(currentUser.id, 0);
      } catch (error) {
        const errorMessage =
          error.response?.data?.message ||
          (isEditing
            ? "Failed to update task."
            : "Failed to add task. Please try again.");
        setSubmitError(errorMessage);
        console.error("Error submitting task:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEditTask = (task) => {
    setIsEditing(true);
    setEditingTask(task);
    setFormData({
      taskId: task.id || "",
      title: task.title || "",
      link: task.link || "",
      storyPoints: task.storyPoints || task.actualPoints || "",
      dueDate: task.dueDate ? dayjs(task.dueDate) : dayjs(),
    });
    setOpenForm(true);
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    setIsEditing(false);
    setEditingTask(null);
    setFormData({
      taskId: "",
      title: "",
      link: "",
      storyPoints: "",
      dueDate: dayjs(),
    });
    setErrors({});
    setSubmitError("");
  };

  const eligibilitySoft = `${eligibility.color}1a`;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      {/* Main Container */}
      <Box
        sx={{
          maxWidth: "1200px",
          width: "100%",
          margin: "0 auto",
          py: { xs: 8, sm: 10 },
          px: { xs: 2, sm: 3, md: 4 },
          mt: { xs: 2, sm: 0 },
          boxSizing: "border-box",
          overflowX: "hidden",
        }}
      >
        <PageHeader
          title="Employee Metrics Dashboard"
          subtitle="Track your Jira tasks, story points, and bonus eligibility"
        />

        {/* Cycle Info and Send Report Button */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "stretch", sm: "center" },
            gap: 2,
            p: { xs: 2.5, sm: 3 },
            mb: 3,
            borderRadius: ui.radius,
            color: "#fff",
            background: "linear-gradient(135deg, #009db9 0%, #007a8a 100%)",
            boxShadow: "0 8px 24px rgba(0, 157, 185, 0.25)",
          }}
        >
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}
          >
            <Box
              sx={{
                flexShrink: 0,
                width: 44,
                height: 44,
                borderRadius: "12px",
                backgroundColor: "rgba(255,255,255,0.18)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <EventRepeatOutlinedIcon />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                Current cycle
              </Typography>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "1rem", sm: "1.15rem" },
                  lineHeight: 1.3,
                }}
              >
                {currentCycle.display}
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            onClick={handleSendReport}
            disabled={isSendingReport}
            startIcon={
              isSendingReport ? (
                <CircularProgress size={18} sx={{ color: "inherit" }} />
              ) : (
                <SendOutlinedIcon fontSize="small" />
              )
            }
            sx={{
              backgroundColor: "#fff",
              color: ui.primaryDark,
              fontWeight: 700,
              textTransform: "none",
              borderRadius: "10px",
              boxShadow: "none",
              py: 1.25,
              px: 3,
              flexShrink: 0,
              "&:hover": {
                backgroundColor: ui.primarySoft,
                boxShadow: "none",
              },
              "&.Mui-disabled": {
                backgroundColor: "rgba(255,255,255,0.6)",
                color: ui.primaryDark,
              },
            }}
          >
            {isSendingReport ? "Sending..." : "Send metrics report"}
          </Button>
        </Box>

        {/* Alert Section */}
        {isSaturdayOfWeek2() && (
          <Alert
            severity={
              eligibility.status === "Eligible" ||
              eligibility.status === "Eligible +"
                ? "success"
                : "warning"
            }
            variant="filled"
            sx={{
              mb: 3,
              borderRadius: "10px",
              width: "100%",
              fontSize: { xs: "0.85rem", sm: "0.95rem" },
            }}
          >
            {eligibility.status === "Eligible" ||
            eligibility.status === "Eligible +"
              ? "You're eligible for a bonus this Half Month"
              : "You missed the target."}
          </Alert>
        )}

        {/* Stats Cards */}
        <StatGrid data-tour="em-stats">
          <StatCard
            label="Total Story Points"
            value={totalStoryPoints}
            icon={StarsOutlinedIcon}
          />
          <StatCard
            label="Completed Tasks"
            value={completedTasksCount}
            icon={TaskAltOutlinedIcon}
            tone="success"
          />
          <StatCard
            label="Calendar"
            value={currentDate}
            icon={CalendarTodayOutlinedIcon}
            tone="warning"
          />
        </StatGrid>

        {/* Main Content Area - Tasks and Bonus Tracker */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            gap: { xs: 3, lg: 3 },
            alignItems: "flex-start",
            width: "100%",
          }}
        >
          {/* Tasks Section */}
          <Box
            sx={{
              flex: { xs: "1", lg: "1 1 58%" },
              minWidth: 0,
              width: "100%",
            }}
          >
            <SectionCard
              title="Recent tasks"
              action={
                <Button
                  variant="contained"
                  startIcon={<span>＋</span>}
                  sx={primaryButtonSx}
                  onClick={() => setOpenForm(true)}
                  data-tour="em-add-task"
                >
                  Add New Task
                </Button>
              }
            >
              <TaskList
                isTasksLoading={isTasksLoading}
                page={page}
                tasks={tasks}
                tasksError={tasksError}
                hasMore={hasMore}
                isMobile={isMobile}
                lastTaskElementRef={lastTaskElementRef}
                handleCompleteTask={handleCompleteTask}
                handleEditTask={handleEditTask}
              />
            </SectionCard>
          </Box>

          {/* Bonus Tracker Section */}
          <Box
            sx={{
              flex: { xs: "1", lg: "1 1 38%" },
              minWidth: 0,
              width: "100%",
              position: { lg: "sticky" },
              top: { lg: 24 },
            }}
            data-tour="em-bonus-tracker"
          >
            <SectionCard title="Bonus tracker">
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 1,
                  mb: 1.5,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: ui.text,
                    fontSize: { xs: "0.9rem", sm: "1rem" },
                  }}
                >
                  Progress: {Math.round(progressPercentage)}%
                </Typography>
                <Chip
                  size="small"
                  label={eligibility.status}
                  sx={{
                    fontWeight: 700,
                    borderRadius: "8px",
                    backgroundColor: eligibilitySoft,
                    color: eligibility.color,
                  }}
                />
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.min(100, progressPercentage)}
                sx={{
                  height: "12px",
                  borderRadius: "8px",
                  mb: 1,
                  backgroundColor: "#e5e7eb",
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: eligibility.color,
                    borderRadius: "8px",
                  },
                }}
              />
              <Typography
                variant="caption"
                sx={{ color: ui.muted, fontWeight: 500 }}
              >
                Target: {TARGET_POINTS} points
              </Typography>

              <Box
                sx={{
                  mt: 2.5,
                  p: 2,
                  borderRadius: "12px",
                  backgroundColor: ui.surface,
                  border: `1px solid ${ui.border}`,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{
                      color: ui.muted,
                      fontSize: { xs: "0.9rem", sm: "0.95rem" },
                    }}
                  >
                    Current Points
                  </Typography>
                  <Typography
                    sx={{
                      color: ui.primary,
                      fontWeight: 700,
                      fontSize: { xs: "1.15rem", sm: "1.3rem" },
                    }}
                  >
                    {currentPoints}
                  </Typography>
                </Box>
                <Divider />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{
                      color: ui.muted,
                      fontSize: { xs: "0.9rem", sm: "0.95rem" },
                    }}
                  >
                    Points Needed
                  </Typography>
                  <Typography
                    sx={{
                      color: pointsNeeded > 0 ? "#f57105" : ui.success,
                      fontWeight: 700,
                      fontSize: { xs: "1.15rem", sm: "1.3rem" },
                    }}
                  >
                    {pointsNeeded}
                  </Typography>
                </Box>
                <Divider />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{
                      color: ui.muted,
                      fontSize: { xs: "0.9rem", sm: "0.95rem" },
                    }}
                  >
                    Target
                  </Typography>
                  <Typography
                    sx={{
                      color: ui.danger,
                      fontWeight: 700,
                      fontSize: { xs: "1.15rem", sm: "1.3rem" },
                    }}
                  >
                    {TARGET_POINTS}
                  </Typography>
                </Box>
              </Box>

              <Button
                variant="outlined"
                fullWidth
                startIcon={<FeedbackOutlinedIcon fontSize="small" />}
                sx={{ ...secondaryButtonSx, mt: 2.5, py: 1.25 }}
                onClick={handleOpenManagerFeedback}
              >
                Manager Feedback
              </Button>
            </SectionCard>
          </Box>
        </Box>

        {/* Info Section */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mt: 4,
            width: "100%",
          }}
        >
          <InfoOutlinedIcon sx={{ color: ui.muted, fontSize: "1.1rem" }} />
          <Typography
            variant="body2"
            sx={{
              color: ui.muted,
              fontSize: { xs: "0.8rem", sm: "0.9rem" },
              lineHeight: 1.5,
            }}
          >
            Track your performance and eligibility for bonuses.
          </Typography>
        </Box>
      </Box>

      <TaskFormDialog
        openForm={openForm}
        handleCloseForm={handleCloseForm}
        isEditing={isEditing}
        formData={formData}
        errors={errors}
        submitError={submitError}
        fetchUserError={fetchUserError}
        loading={loading}
        currentUser={currentUser}
        isMobile={isMobile}
        handleChange={handleChange}
        handleDateChange={handleDateChange}
        handleSubmit={handleSubmit}
      />

      <CompleteTaskDialog
        openCompleteDialog={openCompleteDialog}
        setOpenCompleteDialog={setOpenCompleteDialog}
        completedDate={completedDate}
        setCompletedDate={setCompletedDate}
        handleConfirmCompleteTask={handleConfirmCompleteTask}
        isMobile={isMobile}
      />

      <ManagerFeedbackModal
        open={openManagerFeedback}
        onClose={handleCloseManagerFeedback}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{
          vertical: isMobile ? "bottom" : "top",
          horizontal: "center",
        }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{
            width: "100%",
            borderRadius: "10px",
            fontSize: { xs: "0.85rem", sm: "1rem" },
          }}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </LocalizationProvider>
  );
};

export default EmployeeMetrics;